import type { AxiosHeaders } from 'axios';

import { TENANT_CONST } from '@sapporo/core/constant';
import type { DelegationSession, DelegationTenant } from '../../store';

const AWS_REGION_PATTERN =
  /^(?:af|ap|ca|cn|eu|il|me|mx|sa|us)(?:-(?:gov|iso|isob))?-[a-z]+-\d+$/;
const ACCESS_PATH_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;

const isDelegationTenant = (value: unknown): value is DelegationTenant => {
  if (!value || typeof value !== 'object') return false;

  const tenant = value as Record<string, unknown>;

  return (
    typeof tenant.accessPath === 'string' &&
    ACCESS_PATH_PATTERN.test(tenant.accessPath) &&
    typeof tenant.region === 'string' &&
    AWS_REGION_PATTERN.test(tenant.region) &&
    typeof tenant.tenantID === 'string' &&
    !!tenant.tenantID &&
    typeof tenant.tenantName === 'string' &&
    !!tenant.tenantName &&
    (tenant.tier === TENANT_CONST.TIER.FREETIER ||
      tenant.tier === TENANT_CONST.TIER.STANDARD)
  );
};

export const isDelegationReferrerAllowed = (
  referrer: string,
  adminURL: string,
  isDevelopment: boolean,
) => {
  if (isDevelopment) return true;

  try {
    return new URL(referrer).hostname === new URL(adminURL).hostname;
  } catch {
    return false;
  }
};

export const getRequestTenantID = (
  headers: AxiosHeaders,
  fallbackTenantID: Nullish<string>,
) => {
  const requestTenantID = headers.get('X-Tenant-ID');

  return typeof requestTenantID === 'string' && requestTenantID
    ? requestTenantID
    : (fallbackTenantID ?? undefined);
};

export const parseDelegationSearch = (
  locationSearch: string,
): Nullish<DelegationSession> => {
  const searchParams = new URLSearchParams(locationSearch);
  const adminOtpKey = searchParams.get('delegationOTP');
  const serializedTenant = searchParams.get('tenant');

  if (!adminOtpKey || !serializedTenant) return null;

  try {
    const tenant = JSON.parse(serializedTenant) as unknown;

    if (!isDelegationTenant(tenant)) return null;

    return { adminOtpKey, tenant };
  } catch {
    return null;
  }
};

export const syncAdminOtpHeader = (
  headers: AxiosHeaders,
  adminOtpKey: Nullish<string>,
) => {
  if (adminOtpKey) {
    headers.set('X-Admin-OTP-Key', adminOtpKey);

    return;
  }

  headers.delete('X-Admin-OTP-Key');
};
